export const createDropHandler = ({
	dashboard,
	setDashboard,
	devices,
	mode,
	multiSelectEnabled,
	selectedWidgetIdSet,
	selectedWidgetIds,
	defaultWidth,
	defaultHeight,
	defaultDivWidth,
	defaultDivHeight,
	getDefaultWidgetDimensions,
	getWidgetRectPx,
	getEditableMetrics,
	getMinimumWidgetSizeForType,
	propsFromType,
	dragPointerOffsetRef,
	getDropContainerStyleMetrics,
	getDropWidgetSize,
	getDropBounds,
	getRelativeDropPoint,
	isWidgetFreeMovementEnabled,
	getInitialDropPoint,
	shouldSnapWaterNetworkDropPoint,
	isWaterNetworkWidgetType,
	isWaterNetworkLinkEnabled,
	getSnappedWaterNetworkDropPoint,
	snapWaterNetworkMoveRect,
	isDropCandidateWithinBounds,
	checkOverlap,
	isImmediateDropAllowedWidget,
	getDropSearchStep,
	WATER_NETWORK_FREEFORM_SEARCH_STEP,
	getDropSearchGridSize,
	getNearestAvailableDropCandidate,
	shouldShowNoDropSpaceAlert,
	setAlertMessage,
	t,
	setAlertType,
	setShowAlert,
	getSelectedWidgetIdSet,
	getSelectedWidgetRectEntries,
	getSelectedGroupBounds,
	getClampedSelectedGroupDelta,
	getSelectedGroupMovedRects,
	constrainRectToBackgroundImage,
	storeWidgetPosition,
	sortWidgets,
	getConstrainedOrFallbackRect,
	isInvalidWidgetOverlap,
	buildNewDashboardWidget,
	getDefaultWidgetTitle,}) => (e) => {
		e.preventDefault();
		e.stopPropagation();

		const data = e.dataTransfer.getData('text/plain').split(';');
		if (data.length < 5) return;

		const type = data[0];
		const wid = data[1];
		const parsedOffsetX = Number(data[2]);
		const parsedOffsetY = Number(data[3]);
		const action = data[4];

		const mouseX = e.clientX;
		const mouseY = e.clientY;

		const dashBoardElement = document.querySelector('.editable-dashboard');
		const divertedElement = document.getElementById('diverted');
		if (!dashBoardElement || !divertedElement) return;
		const divertedRect = divertedElement.getBoundingClientRect();
		const metrics = getEditableMetrics();
		if (!metrics) return;

		const defaultWidgetDimensions = getDefaultWidgetDimensions(type, defaultWidth, defaultHeight);
		let widgetWidth = defaultWidgetDimensions.width;
		let widgetHeight = defaultWidgetDimensions.height;

		if (action === 'move') {
			const existingWidget = (dashboard?.widgets || []).find(w => w.id === wid);
			if (existingWidget) {
				const existingWidgetRect = getWidgetRectPx(existingWidget, metrics);
				widgetWidth = existingWidgetRect.width;
				widgetHeight = existingWidgetRect.height;
			}
		}

		const currentWidgets = dashboard?.widgets || [];
		const draggedWidget = action === 'move'
			? currentWidgets.find((widget) => widget.id === wid)
			: { type, extra_props: propsFromType(type) };
		const minimumWidgetSize = getMinimumWidgetSizeForType(
			draggedWidget?.type || type,
			defaultDivWidth,
			defaultDivHeight,
		);

		let offsetX = Number.isFinite(parsedOffsetX) ? parsedOffsetX : 0;
		let offsetY = Number.isFinite(parsedOffsetY) ? parsedOffsetY : 0;
		const capturedOffset = dragPointerOffsetRef.current;
		if (capturedOffset && String(capturedOffset.id) === String(wid)) {
			if (Number.isFinite(capturedOffset.offsetX)) offsetX = capturedOffset.offsetX;
			if (Number.isFinite(capturedOffset.offsetY)) offsetY = capturedOffset.offsetY;
		}
		const maxOffsetX = Number.isFinite(widgetWidth) && widgetWidth > 0 ? widgetWidth : defaultDivWidth;
		const maxOffsetY = Number.isFinite(widgetHeight) && widgetHeight > 0 ? widgetHeight : defaultDivHeight;
		offsetX = Math.max(0, Math.min(offsetX, maxOffsetX));
		offsetY = Math.max(0, Math.min(offsetY, maxOffsetY));

		const intendedLeftGlobal = mouseX - offsetX;
		const intendedTopGlobal = mouseY - offsetY;

		const {
			gap,
			padLeft,
			padRight,
			padTop,
			padBottom,
			dropScrollLeft,
			dropScrollTop,
			effectiveDropWidth,
			effectiveDropHeight,
		} = getDropContainerStyleMetrics({
			divertedElement,
			divertedRect,
			metrics,
		});
		const normalizedDropWidgetSize = getDropWidgetSize({
			widgetWidth,
			widgetHeight,
			minimumWidgetSize,
			effectiveDropWidth,
			effectiveDropHeight,
			padLeft,
			padRight,
			padTop,
			padBottom,
		});
		widgetWidth = normalizedDropWidgetSize.widgetWidth;
		widgetHeight = normalizedDropWidgetSize.widgetHeight;
		const {
			gridColumnSize,
			gridRowSize,
			minLeft,
			maxLeft,
			minTop,
			maxTop,
			usableWidth,
			usableHeight,
		} = getDropBounds({
			effectiveDropWidth,
			effectiveDropHeight,
			widgetWidth,
			widgetHeight,
			padLeft,
			padRight,
			padTop,
			padBottom,
			defaultDivWidth,
			defaultDivHeight,
			gap,
		});

		const relativeDropPoint = getRelativeDropPoint({
			intendedLeftGlobal,
			intendedTopGlobal,
			divertedRect,
			dropScrollLeft,
			dropScrollTop,
		});
		let initialDroppedLeftRelative = relativeDropPoint.left;
		let initialDroppedTopRelative = relativeDropPoint.top;

		const isFreeformLayoutWidget = isWidgetFreeMovementEnabled(draggedWidget);
		const initialDropPoint = getInitialDropPoint({
			relativeDropPoint,
			isFreeformLayoutWidget,
			minLeft,
			maxLeft,
			minTop,
			maxTop,
			gridColumnSize,
			gridRowSize,
		});
		let finalDroppedLeft = initialDropPoint.left;
		let finalDroppedTop = initialDropPoint.top;

		const shouldSnapDroppedWaterPoint = shouldSnapWaterNetworkDropPoint({
			isFreeformLayoutWidget,
			isWaterNetworkWidget: isWaterNetworkWidgetType(draggedWidget),
			isWaterNetworkLinkEnabled: isWaterNetworkLinkEnabled(draggedWidget),
		});

		if (shouldSnapDroppedWaterPoint) {
			const clampedSnappedWaterPoint = getSnappedWaterNetworkDropPoint({
				left: finalDroppedLeft,
				top: finalDroppedTop,
				width: widgetWidth,
				height: widgetHeight,
				draggedWidget,
				currentWidgets,
				getWidgetRectPx,
				metrics,
				ignoredWidgetId: action === 'move' ? wid : null,
				snapWaterNetworkMoveRect,
				minLeft,
				maxLeft,
				minTop,
				maxTop,
			});
			finalDroppedLeft = clampedSnappedWaterPoint.left;
			finalDroppedTop = clampedSnappedWaterPoint.top;
		}

		let foundValidSpot = false;

		const isInitialSpotValid = (
			isDropCandidateWithinBounds({
				left: finalDroppedLeft,
				top: finalDroppedTop,
				minLeft,
				minTop,
				widgetWidth,
				widgetHeight,
				effectiveDropWidth,
				effectiveDropHeight,
				padRight,
				padBottom,
			}) &&
			!checkOverlap(finalDroppedLeft, finalDroppedTop, widgetWidth, widgetHeight, currentWidgets, wid, draggedWidget)
		);

		if (isImmediateDropAllowedWidget(type) || isInitialSpotValid) {
			foundValidSpot = true;
		} else {
			const { horizontalSearchStep, verticalSearchStep } = getDropSearchStep({
				isFreeformLayoutWidget,
				gridColumnSize,
				gridRowSize,
				freeformSearchStep: WATER_NETWORK_FREEFORM_SEARCH_STEP,
			});
			const { maxCols, maxRows } = getDropSearchGridSize({
				usableWidth,
				usableHeight,
				widgetWidth,
				widgetHeight,
				horizontalSearchStep,
				verticalSearchStep,
			});
			const nearestAvailableDropCandidate = getNearestAvailableDropCandidate({
				maxCols,
				maxRows,
				minLeft,
				minTop,
				horizontalSearchStep,
				verticalSearchStep,
				widgetWidth,
				widgetHeight,
				effectiveDropWidth,
				effectiveDropHeight,
				padRight,
				padBottom,
				initialDroppedLeftRelative,
				initialDroppedTopRelative,
				checkOverlap,
				currentWidgets,
				wid,
				draggedWidget,
			});
			const { bestCandidateX, bestCandidateY } = nearestAvailableDropCandidate;
			foundValidSpot = nearestAvailableDropCandidate.foundValidSpot;

			if (foundValidSpot) {
				finalDroppedLeft = bestCandidateX;
				finalDroppedTop = bestCandidateY;
				if (shouldSnapDroppedWaterPoint) {
					const clampedSnappedFallbackWaterPoint = getSnappedWaterNetworkDropPoint({
						left: finalDroppedLeft,
						top: finalDroppedTop,
						width: widgetWidth,
						height: widgetHeight,
						draggedWidget,
						currentWidgets,
						getWidgetRectPx,
						metrics,
						ignoredWidgetId: action === 'move' ? wid : null,
						snapWaterNetworkMoveRect,
						minLeft,
						maxLeft,
						minTop,
						maxTop,
					});
					finalDroppedLeft = clampedSnappedFallbackWaterPoint.left;
					finalDroppedTop = clampedSnappedFallbackWaterPoint.top;
				}
			}
		}

		if (shouldShowNoDropSpaceAlert({ foundValidSpot, type })) {
			setAlertMessage(t.noSpaceForDuplication || 'No valid space available for this widget.');
			setAlertType('warning');
			setShowAlert(true);
			return;
		}

		if (action === 'move') {
			const shouldMoveSelectedGroup = (
				mode === 'edit' &&
				multiSelectEnabled &&
				selectedWidgetIdSet.has(String(wid)) &&
				selectedWidgetIds.length > 1
			);

			if (shouldMoveSelectedGroup) {
				const selectedIdSet = getSelectedWidgetIdSet(selectedWidgetIds);
				const selectedRects = getSelectedWidgetRectEntries({
					widgets: currentWidgets,
					selectedIdSet,
					getWidgetRectPx,
					metrics,
				});
				const draggedRect = selectedRects.find((entry) => String(entry.widget.id) === String(wid))?.rect;
				const selectedBounds = getSelectedGroupBounds(selectedRects);

				if (!draggedRect || !selectedBounds) {
					return;
				}

				const clampedGroupDelta = getClampedSelectedGroupDelta({
					deltaX: finalDroppedLeft - draggedRect.left,
					deltaY: finalDroppedTop - draggedRect.top,
					bounds: selectedBounds,
					padLeft,
					padRight,
					padTop,
					padBottom,
					effectiveWidth: effectiveDropWidth,
					effectiveHeight: effectiveDropHeight,
				});
				const {
					hasConflict: groupHasConflict,
					movedRectsById,
				} = getSelectedGroupMovedRects({
					selectedRects,
					selectedIdSet,
					currentWidgets,
					deltaX: clampedGroupDelta.x,
					deltaY: clampedGroupDelta.y,
					padLeft,
					padRight,
					padTop,
					padBottom,
					effectiveWidth: effectiveDropWidth,
					effectiveHeight: effectiveDropHeight,
					constrainRectToBackgroundImage,
					checkOverlap,
				});

				if (groupHasConflict) {
					setAlertMessage(t.noSpaceForDuplication || 'No valid space available for the selected widgets.');
					setAlertType('warning');
					setShowAlert(true);
					return;
				}

				const updatedWidgets = currentWidgets.map((widget) => {
					const movedRect = movedRectsById.get(String(widget.id));
					return movedRect ? storeWidgetPosition(widget, movedRect, metrics) : widget;
				});

				setDashboard({
					...dashboard,
					widgets: sortWidgets(updatedWidgets),
				});
				return;
			}

			const constrainedInitialRect = constrainRectToBackgroundImage(
				initialDroppedLeftRelative,
				initialDroppedTopRelative,
				widgetWidth,
				widgetHeight,
				currentWidgets,
				wid,
			);

			if (constrainedInitialRect.constrained) {
				const hasOverlapAtConstrainedSpot = checkOverlap(
					constrainedInitialRect.left,
					constrainedInitialRect.top,
					constrainedInitialRect.width,
					constrainedInitialRect.height,
					currentWidgets,
					wid,
					draggedWidget,
				);

				if (hasOverlapAtConstrainedSpot) {
					const constrainedFallbackRect = constrainRectToBackgroundImage(
						finalDroppedLeft,
						finalDroppedTop,
						widgetWidth,
						widgetHeight,
						currentWidgets,
						wid,
					);
					const hasOverlapAtFallbackSpot = checkOverlap(
						constrainedFallbackRect.left,
						constrainedFallbackRect.top,
						constrainedFallbackRect.width,
						constrainedFallbackRect.height,
						currentWidgets,
						wid,
						draggedWidget,
					);

					if (hasOverlapAtFallbackSpot) {
						setAlertMessage(
							t.noValidSpaceOnBackgroundImage
							|| 'No valid space available inside the background image for this widget.'
						);
						setAlertType('warning');
						setShowAlert(true);
						return;
					}

					const nearestWidgets = currentWidgets.map(wd => wd.id === wid
						? storeWidgetPosition(wd, constrainedFallbackRect, metrics)
						: wd);

					setDashboard({
						...dashboard,
						widgets: sortWidgets(nearestWidgets),
					});
					return;
				}

				const updatedWidgets = currentWidgets.map(wd => wd.id === wid
					? storeWidgetPosition(wd, constrainedInitialRect, metrics)
					: wd);

				setDashboard({
					...dashboard,
					widgets: sortWidgets(updatedWidgets),
				});
				return;
			}

			const updatedWidgets = currentWidgets.map(wd => wd.id === wid
				? storeWidgetPosition(wd, {
					left: finalDroppedLeft,
					top: finalDroppedTop,
					width: widgetWidth,
					height: widgetHeight,
				}, metrics)
				: wd);

			setDashboard({
				...dashboard,
				widgets: sortWidgets(updatedWidgets)
			});
		} else if (action === 'add') {
			const addRect = getConstrainedOrFallbackRect({
				left: finalDroppedLeft,
				top: finalDroppedTop,
				width: widgetWidth,
				height: widgetHeight,
				currentWidgets,
				constrainRectToBackgroundImage,
			});
			if (isInvalidWidgetOverlap({
				rect: addRect,
				currentWidgets,
				draggedWidget,
				type,
				checkOverlap,
			})) {
				setAlertMessage(t.noSpaceForDuplication || 'No valid space available for this widget.');
				setAlertType('warning');
				setShowAlert(true);
				return;
			}

			const newWidget = buildNewDashboardWidget({
				type,
				devices,
				t,
				propsFromType,
				getDefaultWidgetTitle,
				storeWidgetPosition,
				rect: addRect,
				metrics,
			});

			const updatedWidgets = [...currentWidgets, newWidget];
			setDashboard({
				...dashboard,
				widgets: sortWidgets(updatedWidgets)
			});
		}
	};